import PropTypes from 'prop-types';
import EverTrue from 'app';
import moment from 'moment';
import FeatureStore from 'apps/layout/stores/feature-store';
import { useFluxStore } from '@evertrue/et-flux';
import { Profile, Work } from 'clientDecorator';
import ProfileCardHeader from 'apps/profile/components/profile-card-header';
import ProfileCardSubtitle from 'apps/profile/components/profile-card-subtitle';
import Career from 'apps/profile/components/templates/career';
import Address from 'apps/profile/components/templates/address';
import Education from 'apps/profile/components/templates/education';
import Email from 'apps/profile/components/templates/email';
import Phone from 'apps/profile/components/templates/phone';
import BioInfoAll from 'apps/profile/components/bio-info-all';
import { getAddresses, getDefaultAddress } from 'mixins/address-mixin';
import Icon from 'components/elements/icon';
import CDCModalController from 'apps/profile/components/cdc/cdc-modal-controller';
import { ModalWrapper, ModalTriggerDeprecated, ModalDeprecated, ModalConfirm } from '@evertrue/et-components';
import { useGlobalDrawerProvider } from 'components/drawer/context/global-drawer-context';
import { trackOutreachAction } from 'apps/outreach/utils/utils';

const BioInfo = ({ contact = {}, has_cdc_profile = false }) => {
  const { addDrawer } = useGlobalDrawerProvider();
  const hasCdcProfile = useFluxStore(FeatureStore, () => FeatureStore.hasFeature('cdc_profile'));

  const handleTrack = (type) => {
    EverTrue.track.set('contact_action', {
      type,
      referrer: 'profile_section_bio',
    });
  };

  const handleEmailClick = (clickedEmailAddress) => {
    trackOutreachAction('open_emailer');
    addDrawer('emailer', {
      contact,
      clickedEmailAddress,
    });
  };

  const handlePhoneClick = () => {
    const contactInfo = Profile.getContactInfo(contact);
    trackOutreachAction('open_dialer');
    addDrawer('dialer', {
      contact,
      phones: contactInfo.phones || [],
      name: contact.name,
      drawerTitle: 'Dialer',
    });
  };

  const renderFullData = () => (
    <ModalDeprecated width={480} closeOnOutsideClick={false} closeOnEscape={false}>
      <ModalConfirm header="Bio">
        <BioInfoAll contact={contact} />
      </ModalConfirm>
    </ModalDeprecated>
  );

  const renderSuggestModal = () => (
    <ModalWrapper
      width={770}
      isOpen
      closeOnOutsideClick={false}
      render={({ close }) => <CDCModalController contact={contact} onClose={close} referrer="profile_bio_card" />}
    />
  );

  const renderModalTrigger = (count) => {
    if (count > 0) {
      return (
        <ModalTriggerDeprecated modal={renderFullData()}>
          <span className="bio-info--title-more"> ( +{count} more )</span>
        </ModalTriggerDeprecated>
      );
    } else return null;
  };

  const renderSuggestTrigger = () => {
    if (has_cdc_profile || hasCdcProfile) {
      return (
        <ModalTriggerDeprecated modal={renderSuggestModal()} className="bio-info--title-suggest">
          <>
            <Icon icon="upload-contact" before />
            Suggest Contact Update
          </>
        </ModalTriggerDeprecated>
      );
    } else return null;
  };

  const renderContactTitle = (title, count) => (
    <span className="bio-info--contact-title">
      <h1 style={{ fontSize: '14px' }}>
        {title}
        {renderModalTrigger(count)}
      </h1>
      {renderSuggestTrigger()}
    </span>
  );

  const renderTitle = (title, count) => (
    <h1 className="bio-info--title" style={{ fontSize: '14px' }}>
      {title}
      {renderModalTrigger(count)}
    </h1>
  );

  const renderBioTitle = () => {
    const title = (() => {
      let timestamp;
      if ((timestamp = contact.updated_at)) {
        const date = moment(timestamp).format('MMMM Do YYYY, h:mm a');
        const ago = moment(timestamp).fromNow();
        return `Contact Record updated: ${date} \n(${ago})`;
      } else {
        return "no 'updated_at' property found";
      }
    })();
    return (
      <h1 title={title} className="profile-card-header--title">
        Bio
      </h1>
    );
  };

  const getContactInfoCount = () => {
    let count = 0;
    const addresses = getAddresses(contact);
    const contact_info = Profile.getContactInfo(contact);
    count += Math.max(addresses.length - 1, 0);
    count += Math.max((contact_info.phones != null ? contact_info.phones.length : undefined) - 1, 0);
    count += Math.max((contact_info.emails != null ? contact_info.emails.length : undefined) - 1, 0);
    return count;
  };

  const positions = Work.getAllPositions(contact);
  const educations = Profile.getParsedEducation(contact);
  const primary_work = Work.getCurrentCompany(contact);
  const primary_contact_info = Profile.getPrimaryContactInfo(contact);
  const primary_address = getDefaultAddress(contact);
  const primary_education = Profile.getParsedEducation(contact)?.[0];

  return (
    <div className="bio-info">
      <ProfileCardHeader title={renderBioTitle()}>
        <ModalTriggerDeprecated className="pull-right" modal={renderFullData()}>
          View All
        </ModalTriggerDeprecated>
      </ProfileCardHeader>

      <div className="bio-info--section">
        <ProfileCardSubtitle label={renderTitle('Career', positions.length - 1)} />
        <Career data={primary_work} />
      </div>

      <div className="bio-info--section">
        <ProfileCardSubtitle label={renderTitle('Education', educations.length - 1)} />
        <Education data={primary_education} />
      </div>

      <div className="bio-info--section">
        <ProfileCardSubtitle label={renderContactTitle('Primary Contact Info', getContactInfoCount())} />
        <Phone data={primary_contact_info.phone} onClick={handlePhoneClick} />
        <Email data={primary_contact_info.email} onClick={handleEmailClick} />
        <Address data={primary_address} onClick={() => handleTrack('address')} />
      </div>
    </div>
  );
};

BioInfo.propTypes = {
  contact: PropTypes.object,
  has_cdc_profile: PropTypes.bool,
};

BioInfo.defaultProps = {
  contact: {},
  has_cdc_profile: false,
};

export default BioInfo;
